import { useEffect, useState } from "react";
import {
  DOSE_STEP,
  DOSE_UNIT_LABEL,
  DOSE_UNITS,
  type Dose,
  type DoseUnit,
  formatDose,
  MAX_DOSE,
  parseDose,
  roundDose,
  TYPE_EMOJI,
} from "../../../lib/entry-text.ts";
import type { Household } from "../../../lib/queries.ts";
import { clock } from "../../../lib/time.ts";
import { cn } from "../../../lib/utils.ts";
import { Button } from "../../ui/button.tsx";
import { Sheet } from "../../ui/sheet.tsx";

type MedPreset = Household["medPresets"][number];

/**
 * The household's medicines. Tapping one logs its usual dose (set in
 * Settings) in one tap; its Dose button opens a stepper for any other amount.
 */
export function MedsSheet({
  open,
  babyName,
  presets,
  now,
  onClose,
  onLog,
}: {
  open: boolean;
  babyName: string;
  presets: MedPreset[];
  now: Date;
  onClose: () => void;
  onLog: (label: string, dose: Dose | null) => void;
}) {
  const [dosing, setDosing] = useState<MedPreset | null>(null);
  // Each opening starts from the list.
  useEffect(() => {
    if (open) setDosing(null);
  }, [open]);

  return (
    <Sheet open={open} title={`Meds — ${babyName}`} emoji={TYPE_EMOJI.meds} onClose={onClose}>
      {dosing ? (
        <DoseStep
          preset={dosing}
          now={now}
          onBack={() => setDosing(null)}
          onSave={(dose) => onLog(dosing.name, dose)}
        />
      ) : (
        <div className="mt-4 flex flex-col gap-2.5">
          {presets.map((m) => (
            <div key={m.name} className="flex gap-2">
              <Button
                variant="choice"
                className="min-w-0 flex-1"
                onClick={() => onLog(m.name, m.dose)}
              >
                <span className="truncate">{m.name}</span>
                {m.dose ? (
                  <span className="ml-auto shrink-0 pl-2 text-[15px] tabular-nums opacity-70">
                    {formatDose(m.dose.amount, m.dose.unit)}
                  </span>
                ) : null}
              </Button>
              <Button
                variant="secondary"
                className="min-h-[56px] shrink-0 px-3.5 text-[15px]"
                aria-label={`${m.name}, another dose`}
                onClick={() => setDosing(m)}
              >
                Dose
              </Button>
            </div>
          ))}
          {presets.length === 0 ? (
            <p className="text-[14px] italic opacity-60">Add meds presets in Settings.</p>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}

/** How much of one medicine, in which unit; starts from its usual dose. */
function DoseStep({
  preset,
  now,
  onBack,
  onSave,
}: {
  preset: MedPreset;
  now: Date;
  onBack: () => void;
  onSave: (dose: Dose) => void;
}) {
  const [text, setText] = useState(preset.dose ? String(preset.dose.amount) : "");
  const [unit, setUnit] = useState<DoseUnit>(preset.dose?.unit ?? "ml");
  const amount = parseDose(text);

  return (
    <>
      <div className="mt-3 flex items-baseline gap-2">
        <h3 className="heading min-w-0 truncate text-[18px]">{preset.name}</h3>
        <Button variant="ghost" className="ml-auto shrink-0" onClick={onBack}>
          All meds
        </Button>
      </div>
      <DoseFields name={preset.name} text={text} unit={unit} onText={setText} onUnit={setUnit} />
      <Button
        variant="primary"
        className="mt-4 w-full"
        disabled={amount === null}
        onClick={() => amount !== null && onSave({ amount, unit })}
      >
        Save at {clock(now)}
      </Button>
    </>
  );
}

/**
 * A dose's stepper, its typed amount, and the unit chips; the dose step and
 * the edit sheet share it. The text stays as typed, so "2." can become "2.5".
 */
export function DoseFields({
  name,
  text,
  unit,
  onText,
  onUnit,
}: {
  /** The medicine, for the amount's label. */
  name: string;
  text: string;
  unit: DoseUnit;
  onText: (text: string) => void;
  onUnit: (unit: DoseUnit) => void;
}) {
  const amount = parseDose(text);
  const step = DOSE_STEP[unit];

  const bump = (delta: number) => {
    const next = roundDose((amount ?? 0) + delta);
    onText(String(Math.min(MAX_DOSE, Math.max(step, next))));
  };

  return (
    <>
      <div className="mt-3 flex items-center justify-center gap-[22px]">
        <Button
          variant="stepper"
          onClick={() => bump(-step)}
          disabled={amount === null || amount <= step}
          aria-label={`${formatDose(step, unit)} less`}
        >
          –
        </Button>
        <div className="min-w-[120px] text-center">
          <input
            className="heading w-[150px] bg-transparent text-center text-[56px] leading-none tracking-[-0.04em] tabular-nums outline-offset-4"
            value={text}
            onChange={(e) => onText(e.target.value)}
            inputMode="decimal"
            placeholder="0"
            aria-label={`Dose of ${name}, in ${DOSE_UNIT_LABEL[unit]}`}
            aria-invalid={text.trim() !== "" && amount === null}
          />
          <div className="kicker mt-1 text-[12px]">{DOSE_UNIT_LABEL[unit]}</div>
        </div>
        <Button
          variant="stepper"
          onClick={() => bump(step)}
          aria-label={`${formatDose(step, unit)} more`}
        >
          +
        </Button>
      </div>
      <div role="group" aria-label="Unit" className="mt-[18px] flex gap-1.5">
        {DOSE_UNITS.map((u) => (
          <Button
            key={u}
            variant="chip"
            className={cn("min-w-0 px-0 text-[14px]", unit === u && "is-selected")}
            aria-pressed={unit === u}
            onClick={() => onUnit(u)}
          >
            {DOSE_UNIT_LABEL[u]}
          </Button>
        ))}
      </div>
    </>
  );
}
